import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/context/AuthContext';
import { ProjectProvider } from '@/context/ProjectContext';
import { AppLayout, ProtectedRoute } from '@/components/layout';
import {
  LandingPage,
  HowItWorksPage,
  LoginPage,
  RegisterPage,
  DashboardPage,
  ProjectsPage,
  DriversPage,
  SegmentsPage,
  QuestionsPage,
  StakeholdersPage,
  NetworkPage,
  SelfIdentifyPage,
  SurveyPage,
  AboutMePage,
  AboutOthersPage,
  MyDashboardsPage,
  UserProfilePage,
  PublicLandingPage,
  CustomerAccountsPage,
  ManageAccountPage,
  DataAnalysisPage,
  PricingPage,
  AdminPricingPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
  RegistrationVerifyPage,
  InviteVerificationPage,
  OnboardingCarousel,
  HelpPage,
  // Dashboard Pages
  ProjectSummaryPage,
  PublicAnalysisPage,
  DriverAnalysisPage,
  SegmentAnalysisPage,
  UserGalaxyPage2,
  MatrixViewPage,
  DashboardConfigPage,
} from '@/pages';
import '@/App.css';
import AuthCallback from '@/components/AuthCallback';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import AdminHelpPage from '@/pages/admin/AdminHelpPage';
import AdminOnboardingCarousel from '@/pages/AdminOnboardingCarousel';
import ReportPrintPage from '@/pages/ReportPrintPage';

function AppRouter() {
  // CRITICAL: Check for OAuth session_id in URL hash synchronously (before any route)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const hash = window.location.hash;
  if (hash && hash.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
            <Route path="/verify-account" element={<RegistrationVerifyPage />} />
            
            {/* Invite Verification & Onboarding Routes */}
            <Route path="/invite/:inviteToken" element={<InviteVerificationPage />} />
            <Route path="/onboarding/:inviteToken" element={<OnboardingCarousel />} />
            <Route path="/admin-onboarding" element={<AdminOnboardingCarousel />} />
            <Route path="/admin-onboarding/:projectId" element={<AdminOnboardingCarousel />} />
            
            {/* Public Survey Routes */}
            <Route path="/identify/:projectId" element={<SelfIdentifyPage />} />
            <Route path="/survey/:token" element={<SurveyPage />} />
            <Route path="/public/:projectId" element={<PublicLandingPage />} />
            <Route path="/report-print/:projectId" element={<ReportPrintPage />} />

            {/* Protected Routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DashboardPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/projects" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProjectsPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <UserProfilePage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manage-account" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ManageAccountPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            
            {/* Help Page */}
            <Route 
              path="/help" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <HelpPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            
            {/* Project-specific Routes - User Section */}
            <Route 
              path="/project/:projectId/about-me" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AboutMePage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project/:projectId/about-others" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AboutOthersPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project/:projectId/my-dashboards" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <MyDashboardsPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            
            {/* Dashboard Analysis Routes */}
            <Route 
              path="/project/:projectId/dashboards/summary" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProjectSummaryPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project/:projectId/dashboards/summary/configure" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DashboardConfigPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project/:projectId/dashboards/public-analysis" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <PublicAnalysisPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project/:projectId/dashboards/driver-analysis" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DriverAnalysisPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project/:projectId/dashboards/segment-analysis" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SegmentAnalysisPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project/:projectId/dashboards/galaxy" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <UserGalaxyPage2 />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project/:projectId/dashboards/matrix" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <MatrixViewPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            
            {/* Project-specific Routes - Admin Section */}
            <Route 
              path="/project/:projectId/drivers" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DriversPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project/:projectId/segments" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SegmentsPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project/:projectId/questions" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <QuestionsPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project/:projectId/stakeholders" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <StakeholdersPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project/:projectId/admin-dashboard" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdminDashboardPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project/:projectId/admin-help" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdminHelpPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />

            {/* Global Admin Routes */}
            <Route 
              path="/admin/customers" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <CustomerAccountsPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/data-analysis" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DataAnalysisPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/pricing" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdminPricingPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />

            {/* Catch-all redirect */}
            <Route path="/payment/success" element={
                <PaymentSuccessPage />
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProjectProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </ProjectProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
